import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api, getErrorMessage } from "@/lib/api";
import { CATEGORIZED_MASTER_GROUPS, MASTER_CATEGORIES, MASTER_TYPES } from "@/lib/constants";

export default function AdminMasterData() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(MASTER_TYPES[0].key);
  const [newValue, setNewValue] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [valueSearch, setValueSearch] = useState("");

  const load = useCallback(() => {
    api
      .get("/master-data/all")
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!newValue.trim()) return;
    const catOptions = CATEGORIZED_MASTER_GROUPS[activeType];
    if (catOptions && !newCategory) return toast.error("Choose a category for this entry");
    setBusy(true);
    try {
      const payload = { type: activeType, value: newValue.trim() };
      if (catOptions && newCategory) payload.category = newCategory;
      await api.post("/master-data", payload);
      toast.success(`Added \"${newValue.trim()}\"`);
      setNewValue("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const changeCategory = async (item, category) => {
    try {
      await api.put(`/master-data/${item.id}`, { category });
      toast.success("Category updated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const toggle = async (item) => {
    try {
      await api.put(`/master-data/${item.id}`, { active: !item.active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const saveEdit = async (item) => {
    if (!editValue.trim() || editValue.trim() === item.value) {
      setEditingId(null);
      return;
    }
    try {
      await api.put(`/master-data/${item.id}`, { value: editValue.trim() });
      toast.success("Value updated");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const move = async (item, direction) => {
    const list = items.filter((i) => i.type === activeType);
    const idx = list.findIndex((i) => i.id === item.id);
    const target = idx + direction;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    // optimistic update
    const others = items.filter((i) => i.type !== activeType);
    setItems([...others, ...reordered]);
    try {
      await api.post("/master-data/reorder", { type: activeType, ordered_ids: reordered.map((i) => i.id) });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
      load();
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const activeLabel = MASTER_TYPES.find((t) => t.key === activeType)?.label || activeType;
  const catOptions = CATEGORIZED_MASTER_GROUPS[activeType];
  const filtered = items.filter((i) => i.type === activeType)
    .filter((i) => !valueSearch || i.value.toLowerCase().includes(valueSearch.toLowerCase()));
  const visibleTypes = MASTER_TYPES.filter((t) => !groupSearch || t.label.toLowerCase().includes(groupSearch.toLowerCase()));

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-master-data-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Master Data</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Configure every dropdown used across the platform — add, edit, disable and reorder values. No code changes needed. Entries are deactivated, never deleted.
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="master-data-group-search" placeholder="Search groups (e.g. Cities, Skills, Payment Terms)..." className="pl-9 bg-white h-9" value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} />
      </div>

      <div className="space-y-4" data-testid="admin-master-data-tabs">
        {MASTER_CATEGORIES.map((cat) => {
          const catTypes = visibleTypes.filter((t) => t.category === cat);
          if (catTypes.length === 0) return null;
          return (
            <div key={cat}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{cat}</p>
              <div className="flex flex-wrap gap-2">
                {catTypes.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => { setActiveType(t.key); setValueSearch(""); setNewCategory(""); }}
                    data-testid={`master-data-tab-${t.key}`}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      activeType === t.key
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Card className="rounded-xl border-border shadow-none max-w-3xl">
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="master-data-new-input"
              placeholder={`Add a new entry to ${activeLabel}...`}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            {catOptions && (
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger className="sm:w-48 shrink-0" data-testid="master-data-new-category-select">
                  <SelectValue placeholder="Category..." />
                </SelectTrigger>
                <SelectContent>
                  {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button onClick={add} disabled={busy} className="rounded-lg shrink-0" data-testid="master-data-add-button">
              <Plus className="h-4 w-4 mr-1.5" /> Add
            </Button>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="master-data-value-search" placeholder={`Search within ${activeLabel}...`} className="pl-9 h-9" value={valueSearch} onChange={(e) => setValueSearch(e.target.value)} />
          </div>

          <div className="space-y-2" data-testid="master-data-list">
            {filtered.length === 0 && <p className="text-sm text-muted-foreground">No entries yet in {activeLabel}.</p>}
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-2"
                data-testid="master-data-item"
              >
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(item, -1)} disabled={idx === 0} aria-label="Move up"
                    className="text-muted-foreground hover:text-foreground disabled:opacity-25" data-testid={`master-data-up-${item.id}`}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => move(item, 1)} disabled={idx === filtered.length - 1} aria-label="Move down"
                    className="text-muted-foreground hover:text-foreground disabled:opacity-25" data-testid={`master-data-down-${item.id}`}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
                {editingId === item.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      className="h-8 text-sm"
                      value={editValue}
                      autoFocus
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveEdit(item); if (e.key === "Escape") setEditingId(null); }}
                      data-testid={`master-data-edit-input-${item.id}`}
                    />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => saveEdit(item)} aria-label="Save" data-testid={`master-data-edit-save-${item.id}`}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)} aria-label="Cancel">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className={`flex-1 text-sm font-medium ${!item.active ? "text-muted-foreground line-through" : ""}`}>
                      {item.value}
                    </span>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => { setEditingId(item.id); setEditValue(item.value); }} aria-label={`Edit ${item.value}`}
                      data-testid={`master-data-edit-${item.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                {catOptions && (
                  <Select value={item.category || ""} onValueChange={(v) => changeCategory(item, v)}>
                    <SelectTrigger className="h-7 w-40 text-xs shrink-0" data-testid={`master-data-category-${item.id}`}>
                      <SelectValue placeholder="Category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {!item.active && (
                  <Badge variant="outline" className="rounded-full text-[10px] bg-muted">Inactive</Badge>
                )}
                <Switch
                  checked={item.active}
                  onCheckedChange={() => toggle(item)}
                  aria-label="Toggle active"
                  data-testid={`master-data-toggle-${item.id}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
